import React, { useMemo, useEffect } from 'react';
import DesktopLayout from '../layouts/DesktopLayout';
import MobileLayout from '../layouts/MobileLayout';
import { useConfig } from '../config/ConfigContext';
import FloatingShareButton from '../components/FloatingShareButton';
import { metaTagsService } from '../services/metaTagsService';
import { CreditCard, Smartphone, Building } from 'lucide-react';

/**
 * Support Page Component
 * Clean design with three main payment options
 * @returns {JSX.Element}
 */
const SupportPage = () => {
  // Detect if the user is on a mobile device
  const isMobile = window.innerWidth <= 768;

  // Get config and theme data
  const { config, theme, loading, error } = useConfig();

  // Update meta tags when component mounts or config changes
  useEffect(() => {
    if (config && !loading) {
      const logoUrl = config.logo_url || `${window.location.origin}/images/penta-lectures-logo.png`;
      
      metaTagsService.updateMetaTags({
        title: `Support - ${config.name || 'Penta University'}`,
        description: `Support ${config.name || 'our educational mission'} through various payment methods`,
        image: logoUrl,
        url: `${window.location.origin}/support`,
        type: 'website'
      });
    }

    // Cleanup function to reset meta tags when component unmounts
    return () => {
      if (config) {
        metaTagsService.resetToDefaults();
      }
    };
  }, [config, loading]);

  // Process payment options - extract only Zelle, Stripe, and PayPal
  const paymentOptions = useMemo(() => {
    if (!config || !config.support_options) {
      return { zelle: null, stripe: null, paypal: null };
    }
    
    const options = config.support_options;
    console.log('[SupportPage] Raw support_options:', options);
    const result = { zelle: null, stripe: null, paypal: null };
    
    // Find each payment method, ignoring "_enabled" keys
    for (const key in options) {
      const lowerKey = key.toLowerCase();
      const option = options[key];
      
      // Skip keys that end with "_enabled" or don't have payment info
      if (key.includes('_enabled') || key.includes('enabled')) {
        continue;
      }
      
      // Skip if option is not an object or is null
      if (!option || typeof option !== 'object') {
        continue;
      }
      
      // Skip if no actual payment details
      if (!option.email && !option.link && !option.details && !option.qr_code_url) {
        continue;
      }
      
      if (lowerKey.includes('zelle')) {
        result.zelle = option;
      } else if (lowerKey.includes('stripe')) {
        result.stripe = option;
      } else if (lowerKey.includes('paypal')) {
        result.paypal = option;
      }
    }
    
    console.log('[SupportPage] Extracted payment options:', result);
    return result;
  }, [config]);

  // Extract bank transfer info
  const bankTransfer = useMemo(() => {
    if (!config || !config.other_donation_methods) {
      return null;
    }
    return config.other_donation_methods.bank_transfer || null;
  }, [config]);

  // Handle loading and error states
  if (loading) {
    const Layout = isMobile ? MobileLayout : DesktopLayout;
    return <Layout><div style={{ padding: '20px', textAlign: 'center' }}>Loading Support content...</div></Layout>;
  }

  if (error || !config || !config.support_enabled) { 
    const Layout = isMobile ? MobileLayout : DesktopLayout;
    const errorMessage = error ? `Error: ${error.message}` : 'Support content could not be loaded or is disabled.';
    return <Layout><div style={{ padding: '20px', textAlign: 'center' }}>{errorMessage} Please try again later.</div></Layout>;
  }

  // Provide default title/description
  const pageTitle = config.support_title || 'Support Our Mission';
  const pageDescription = config.support_description || 'Help us spread the teachings of Ahlul Bayt AS';

  // Content for the Support page
  const pageContent = (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '20px' : '40px',
    }}>
      {/* Hero Banner */}
      <div style={{
        background: `linear-gradient(135deg, ${theme?.colors?.primary || '#1e4273'} 0%, #2c5daa 100%)`,
        borderRadius: '16px',
        padding: isMobile ? '40px 24px' : '60px 48px',
        marginBottom: '50px',
        textAlign: 'center',
        color: 'white',
        boxShadow: '0 15px 30px rgba(0, 0, 0, 0.1)'
      }}>
        <h1 style={{ 
          fontSize: isMobile ? '2.2rem' : '3rem', 
          marginBottom: '16px',
          fontWeight: '700',
          letterSpacing: '-0.02em'
        }}>
          {pageTitle}
        </h1>
        <p style={{ 
          fontSize: isMobile ? '1.1rem' : '1.3rem', 
          opacity: '0.95', 
          maxWidth: '800px', 
          margin: '0 auto',
          lineHeight: '1.6'
        }}>
          {pageDescription}
        </p>
      </div>

      {/* Three Main Payment Options */}
      <div style={{ marginBottom: '60px' }}>
        <h2 style={{ 
          color: theme?.colors?.primary || '#1e4273', 
          fontSize: isMobile ? '1.8rem' : '2rem', 
          marginBottom: '30px', 
          textAlign: 'center',
          fontWeight: '600' 
        }}>
          Choose Your Donation Method
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '30px',
          marginBottom: '40px'
        }}>
          {/* Zelle Card */}
          {paymentOptions.zelle && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              border: '1px solid #e5e7eb'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 14px 30px rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.05)';
            }}>
              <div style={{ textAlign: 'center' }}>
                {/* Zelle Logo/Icon */}
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 20px',
                  backgroundColor: '#6B46C1',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Smartphone style={{ width: '40px', height: '40px', color: 'white' }} />
                </div>
                
                <h3 style={{
                  color: theme?.colors?.primary || '#1e4273',
                  fontSize: '1.5rem',
                  marginBottom: '16px',
                  fontWeight: '600'
                }}>
                  Zelle
                </h3>
                
                <p style={{
                  color: '#6b7280',
                  fontSize: '1rem',
                  marginBottom: '24px',
                  lineHeight: '1.6'
                }}>
                  Send one-time or set up monthly recurring payments through your banking app
                </p>
                
                {/* Email Display */}
                {paymentOptions.zelle.email && (
                  <div style={{
                    backgroundColor: '#f3f4f6',
                    padding: '16px',
                    borderRadius: '10px',
                    marginBottom: '20px'
                  }}>
                    <p style={{
                      margin: '0 0 8px 0',
                      fontSize: '0.9rem',
                      color: '#6b7280'
                    }}>
                      Send payment to:
                    </p>
                    <p style={{
                      fontFamily: 'monospace',
                      fontSize: '1.1rem',
                      color: '#111827',
                      fontWeight: '600',
                      margin: 0,
                      wordBreak: 'break-all'
                    }}>
                      {paymentOptions.zelle.email}
                    </p>
                  </div>
                )}
                
                {/* QR Code */}
                {paymentOptions.zelle.qr_code_url && (
                  <div style={{
                    width: '180px',
                    height: '180px',
                    margin: '0 auto',
                    padding: '10px',
                    backgroundColor: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px'
                  }}>
                    <img
                      src={paymentOptions.zelle.qr_code_url}
                      alt="Zelle QR Code"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Stripe Card */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            border: '1px solid #e5e7eb'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 14px 30px rgba(0, 0, 0, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.05)';
          }}>
            <div style={{ textAlign: 'center' }}>
              {/* Credit Card Icon */}
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 20px',
                backgroundColor: '#635BFF',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CreditCard style={{ width: '40px', height: '40px', color: 'white' }} />
              </div>
              
              <h3 style={{
                color: theme?.colors?.primary || '#1e4273',
                fontSize: '1.5rem',
                marginBottom: '16px',
                fontWeight: '600'
              }}>
                Credit/Debit Card
              </h3>
              
              <p style={{
                color: '#6b7280',
                fontSize: '1rem',
                marginBottom: '24px',
                lineHeight: '1.6'
              }}>
                Support with Credit/Debit Card - Monthly subscriptions or one-time donations
              </p>
              
              {/* Monthly Support Link */}
              {paymentOptions.stripe?.link && (
                <a
                  href={paymentOptions.stripe.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '14px 24px',
                    marginBottom: '16px',
                    backgroundColor: '#635BFF',
                    color: 'white',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontSize: '1rem',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(99, 91, 255, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Set Up Monthly Support →
                </a>
              )}
              
              {/* Stripe QR Code - Now after monthly option */}
              {paymentOptions.stripe?.qr_code_url && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{
                    width: '180px',
                    height: '180px',
                    margin: '0 auto',
                    padding: '10px',
                    backgroundColor: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px'
                  }}>
                    <img
                      src={paymentOptions.stripe.qr_code_url}
                      alt="Credit Card Payment QR Code"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                  <p style={{
                    marginTop: '8px',
                    fontSize: '0.85rem',
                    color: '#6b7280',
                    textAlign: 'center'
                  }}>
                    Scan to donate
                  </p>
                </div>
              )}
              
              {/* Divider */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                margin: '20px 0',
                gap: '10px'
              }}>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }} />
                <span style={{ color: '#9ca3af', fontSize: '0.9rem' }}>OR</span>
                <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }} />
              </div>
              
              {/* One-Time Quick Checkout - Simple button */}
              <button
                onClick={async () => {
                  try {
                    const response = await fetch('/.netlify/functions/create-donation-checkout', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        amount: 50, // Default amount
                        announcementId: 'support-stripe-onetime',
                        announcementTitle: `Support ${config?.name || 'Dar-e-Zainab (AS)'}`,
                        centerCode: config?.code || 'DEZ',
                        centerName: config?.name || 'Dar-e-Zainab (AS)',
                      }),
                    });
                    const data = await response.json();
                    if (data?.url) {
                      window.location.href = data.url;
                    }
                  } catch (error) {
                    console.error('Checkout error:', error);
                    alert('Failed to create checkout session. Please try again.');
                  }
                }}
                style={{
                  width: '100%',
                  padding: '14px 24px',
                  backgroundColor: 'transparent',
                  color: '#635BFF',
                  border: '2px solid #635BFF',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#635BFF';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#635BFF';
                }}
              >
                1-Click Donation
              </button>
            </div>
          </div>

          {/* PayPal Card */}
          {paymentOptions.paypal && (
            <div style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              border: '1px solid #e5e7eb'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 14px 30px rgba(0, 0, 0, 0.08)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 25px rgba(0, 0, 0, 0.05)';
            }}>
              <div style={{ textAlign: 'center' }}>
                {/* PayPal Logo */}
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 20px',
                  backgroundColor: '#0070BA',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <span style={{
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    fontFamily: 'Arial, sans-serif'
                  }}>
                    PP
                  </span>
                </div>
                
                <h3 style={{
                  color: theme?.colors?.primary || '#1e4273',
                  fontSize: '1.5rem',
                  marginBottom: '16px',
                  fontWeight: '600'
                }}>
                  PayPal
                </h3>
                
                <p style={{
                  color: '#6b7280',
                  fontSize: '1rem',
                  marginBottom: '24px',
                  lineHeight: '1.6'
                }}>
                  Credit card payments - Set up monthly, weekly, or make one-time donations through PayPal
                </p>
                
                {paymentOptions.paypal.link && (
                  <a
                    href={paymentOptions.paypal.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      width: '100%',
                      padding: '14px 24px',
                      backgroundColor: '#0070BA',
                      color: 'white',
                      borderRadius: '8px',
                      textDecoration: 'none',
                      fontSize: '1rem',
                      fontWeight: '600',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 112, 186, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Donate via PayPal →
                  </a>
                )}
                
                {paymentOptions.paypal.note && (
                  <p style={{
                    marginTop: '16px',
                    fontSize: '0.9rem',
                    color: '#6b7280',
                    lineHeight: '1.5'
                  }}>
                    {paymentOptions.paypal.note}
                  </p>
                )}
                
                {/* PayPal QR Code */}
                {paymentOptions.paypal.qr_code_url && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{
                      width: '180px',
                      height: '180px',
                      margin: '0 auto',
                      padding: '10px',
                      backgroundColor: 'white',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px'
                    }}>
                      <img
                        src={paymentOptions.paypal.qr_code_url}
                        alt="PayPal QR Code"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                    <p style={{
                      marginTop: '8px',
                      fontSize: '0.85rem',
                      color: '#6b7280',
                      textAlign: 'center'
                    }}>
                      Scan to donate via PayPal
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bank Transfer Section */}
      {bankTransfer && (
        <div style={{ 
          marginBottom: '60px', 
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
          border: '1px solid #e5e7eb'
        }}>
          <h2 style={{ 
            color: theme?.colors?.primary || '#1e4273', 
            fontSize: isMobile ? '1.6rem' : '1.8rem', 
            marginBottom: '24px', 
            textAlign: 'center',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px'
          }}>
            <Building style={{ width: '28px', height: '28px' }} />
            Bank Transfer
          </h2>
          
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <p style={{
              textAlign: 'center',
              color: '#6b7280',
              marginBottom: '24px',
              fontSize: '1rem',
              lineHeight: '1.6'
            }}>
              For direct bank transfers, use the following information:
            </p>
            
            <div style={{
              backgroundColor: '#f9fafb',
              padding: '24px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              {bankTransfer.account_number && (
                <div style={{
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <span style={{ 
                    fontWeight: '600', 
                    color: '#374151',
                    fontSize: '1rem'
                  }}>
                    Account Number:
                  </span>
                  <span style={{
                    fontFamily: 'monospace',
                    fontSize: '1.1rem',
                    color: '#111827',
                    fontWeight: '500'
                  }}>
                    {bankTransfer.account_number}
                  </span>
                </div>
              )}
              
              {bankTransfer.routing_number && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <span style={{ 
                    fontWeight: '600', 
                    color: '#374151',
                    fontSize: '1rem'
                  }}>
                    Routing Number:
                  </span>
                  <span style={{
                    fontFamily: 'monospace',
                    fontSize: '1.1rem',
                    color: '#111827',
                    fontWeight: '500'
                  }}>
                    {bankTransfer.routing_number}
                  </span>
                </div>
              )}
            </div>
            
            <p style={{
              marginTop: '16px',
              textAlign: 'center',
              fontSize: '0.9rem',
              color: '#6b7280',
              fontStyle: 'italic'
            }}>
              You can set up recurring monthly transfers through your bank's online portal
            </p>
          </div>
        </div>
      )}

      {/* Security Note */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '30px', 
        color: '#6b7280', 
        fontSize: '0.95rem',
        lineHeight: '1.6'
      }}>
        <p>
          🔒 All transactions are secure and encrypted.
          <br />
          For questions about donations, please contact us directly.
        </p>
      </div>
    </div>
  );

  // Choose layout based on device
  const Layout = isMobile ? MobileLayout : DesktopLayout;
  return (
    <Layout>
      {pageContent}
      <FloatingShareButton 
        pageTitle={`Support - ${config?.name || ''}`}
        pageDescription={pageDescription}
      />
    </Layout>
  );
};

export default SupportPage;